<template>
    <div>
        <v-card
            flat>
            <v-card-title>
                {{ $t('TRUCKS_PAGE_TITLE') }}
                <v-spacer></v-spacer>
                <v-text-field
                    :label="$t('TRUCKS_PAGE_SEARCH')"
                    v-model="search"
                    hide-details
                    class="ml-4"
                    @keypress.enter="createdDataFetch()">
                </v-text-field>
                <v-menu
                    offset-y>
                    <template v-slot:activator="{on}">
                        <v-btn
                            icon
                            v-on="on"
                            class="mx-1">
                            <v-icon>mdi-dots-vertical</v-icon>
                        </v-btn>
                    </template>
                    <v-list dense >
                        <v-list-item
                            link
                            v-if="authorizations.trucks.create"
                            @click="openTruckDialog({editMode: false})"
                            >
                            <v-list-item-avatar>
                               <v-icon>mdi-plus-circle</v-icon>
                            </v-list-item-avatar>
                            <v-list-item-title>
                                {{ $t('new_truck') }}
                            </v-list-item-title>
                        </v-list-item>
                         <v-list-item
                            v-if="authorizations.trucks.update && selected.truck.length == 1"
                            link
                            @click="openTruckDialog({editMode: true, id: selected.truck[0].id})"
                            :disabled="!selected.truck[0].meta.editable">
                            <v-list-item-avatar>
                                <v-icon :disabled="!selected.truck[0].meta.editable">mdi-pencil</v-icon>
                            </v-list-item-avatar>
                            <v-list-item-content>
                                <v-list-item-title>{{ $t('ACTIONS_MENU_EDIT') }}</v-list-item-title>
                            </v-list-item-content>
                        </v-list-item>
                        <v-list-item
                            v-if="authorizations.trucks.delete && selected.truck.length == 1"
                            link
                            @click="openDeleteDecisionDialog(selected.truck[0].id)"
                            :disabled="!selected.truck[0].meta.deletable">
                            <v-list-item-avatar>
                                <v-icon :disabled="!selected.truck[0].meta.deletable">mdi-delete</v-icon>
                            </v-list-item-avatar>
                            <v-list-item-content>
                                <v-list-item-title>{{ $t('ACTIONS_MENU_DELETE') }}</v-list-item-title>
                            </v-list-item-content>
                        </v-list-item>
                    </v-list>
                </v-menu>
            </v-card-title>
            <v-card-subtitle>{{ $t('TRUCKS_PAGE_SUBTITLE') }}</v-card-subtitle>
            <v-divider></v-divider>
            <v-card-text>
                <v-data-table
                    dense
                    single-select
                    show-select
                    v-model="selected.truck"
                    :items="trucks"
                    :headers="trucksHeaders"
                    @click:row="openTruckDisplayDialog"
                    :server-items-length="trucksLength"
                    :options.sync="options"
                    :loading="loading.data">
                    <template v-slot:[`item.track_distance`]="{item}">
                        <v-icon
                            :color="item.track_distance ? 'success' : 'error'">
                            {{ item.track_distance ? "mdi-check" : "mdi-close"}}
                        </v-icon>
                    </template>
                </v-data-table>
            </v-card-text>
        </v-card>
        <TruckDialog
            ref="truckDialog"
            @submit="createdDataFetch()"/>
        <DecisionDialog 
            ref="decisionDialog"
            @submit="submitDecisionDialog"/>
        <AlertDialog
            ref="alertDialog"/>
        <TruckDisplayDialog
            ref="truckDisplayDialog"/>
        <RetryDialog
            ref="retryDialog"
            @submit="onRetryDialogSubmit"/>
    </div>
</template>

<script>
import TruckDialog from '../components/TruckDialog';
import DecisionDialog from '../components/DecisionDialog';
import AlertDialog from '../components/AlertDialog';
import TruckDisplayDialog from '../components/TruckDisplayDialog';
import RetryDialog from '../components/RetryDialog';
import { mapState, mapActions } from 'vuex';
export default {
    name: "Truck",
    components: {
        TruckDialog,
        DecisionDialog,
        AlertDialog,
        TruckDisplayDialog,
        RetryDialog
    },
    data() {
        return {
            trucksLength: 0,
            options: {},
            search: null,
            overlay: false,
            error: {
                data: {
                    state: false,
                    message: null
                }
            },
            selected: {
                truck: []
            },
            trucksHeaders: [
                { text: "", value: "show"},
                { text: this.$t('TRUCK_PLATE'), value: "plate_number"},
                { text: this.$t('TRUCK_TYPE'), value: "truck_type"},
                { text: this.$t("TRUCK_BRAND"), value: "truck_brand"},
                { text: this.$t("TRUCK_YEAR_MAKE"), value: "year_make"},
                { text: this.$t("track_distance"), value: "track_distance"}
            ],
            loading: {
                data: false
            }
        }
    },
    computed: {
        ...mapState("truck", ["trucks"]),
        ...mapState("authentication", ["loggedInUser", "authorizations"])
    },
    watch: {
        options: {
            handler() {
                this.createdDataFetch();
            }
        }
    },
    methods: {
        ...mapActions("truck", ["fetchTrucks", "removeTruck"]),
        openTruckDialog(params) {
            this.$refs.truckDialog.open(params);
        },
        openDeleteDecisionDialog(id) {
            this.$refs.decisionDialog.open({
                title: this.$t('DELETE_CONFIRM_TITLE'),
                content: this.$t('DELETE_CONFIRM_CONTENT'),
                action: "DELETE",
                id: id
            });
        },
        submitDecisionDialog(params) {
            if(params.action == 'DELETE') {
                this.removeTruck(params.id)
                    .then(response => {
                        this.createdDataFetch();
                        this.$refs.alertDialog(this.$t('SUCCESS_TITLE'), response.message);
                    })
                    .catch(error => {
                        this.$refs.alertDialog(this.$t('ERROR_TITLE'), error.message);
                    })
            }
        },
        openTruckDisplayDialog(params) {
            this.$refs.truckDisplayDialog.open(params)
        },
        async createdDataFetch() {
            try {
                this.loading.data = true;
                this.options.search = this.search;
                let response = await this.fetchTrucks(this.options);
                this.trucksLength = response.data.total;
                this.loading.data = false;
            } catch(error) {
                let message = null;
                if(error.response) message = error.response.data.message;
                else message = error;
                this.$refs.retryDialog.open({target: "createdDataFetch", message});
            }
        },
        onRetryDialogSubmit(params) {
            if(params.target == "createdDataFetch") {
                if(params.retry == true) this.createdDataFetch();
            }
        }
    }
}
</script>